// @flow
import React, { useEffect, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router';
import { withLeaflet } from 'react-leaflet';
import type { LeafletContext } from 'react-leaflet/src';
import {
    pointIcon,
    cleanUpNumberedPointIcon,
    numberedPointIcon,
} from 'esm-web-pro/containers/pages/network/Util';
import GroupActionsForm from './GroupActionsForm';
import BaseEditor from '../editor/BaseEditor';

type Props = {
    leaflet: LeafletContext,
    editor: BaseEditor,
}

const GroupObjects = (props: Props) => {
    const { leaflet: { map, map: { _layers } }, editor } = props;
    const history = useHistory();
    const match = useRouteMatch();
    const [group, setGroup] = useState([]);

    // Prevent selected objects from disappearing when the map is being moved
    const highlightCallBack = (layer) => {
        const matchedLayer = group.find((_l) => (
            _l.id === layer.feature.id
        ));
        if (!matchedLayer) return;

        const { type } = layer.feature.geometry;
        if (type === 'LineString') {
            layer.setStyle({ weight: 10 });
        }
        if (type === 'Point') {
            layer.setIcon(numberedPointIcon(layer, '✓', true));
        }
    };

    // Create event listener for layers
    const onLayerClick = (e) => {
        const layer = e.target;
        const { id } = layer.feature;
        const { type } = layer.feature.geometry;
        const isSelected = group.filter((item) => item.id === id).length !== 0;
        if (isSelected) {
            setGroup(group.filter((item) => item.id !== id));
            if (type === 'LineString') {
                layer.setStyle({ weight: 3 });
            }
            if (type === 'Point') {
                layer.setIcon(cleanUpNumberedPointIcon(layer));
            }
        } else {
            setGroup([...group, { type, id }]);
            if (type === 'LineString') {
                layer.setStyle({ weight: 10 });
            }
            if (type === 'Point') {
                layer.setIcon(numberedPointIcon(layer, '✓', true));
            }
        }
    };

    // Check if an object is added to project and disable it if true
    const disableObjects = React.useCallback((filteredLayers) => {
        const filteredPoints = filteredLayers.filter((layer) => layer.feature.geometry.type === 'Point');
        const filteredLines = filteredLayers.filter((layer) => layer.feature.geometry.type === 'LineString');

        filteredPoints.forEach((l) => {
            const { feature: { properties: { point } } } = l;
            let icon;
            // if the point property is null then the object is gispoint and should be disabled
            if (point) {
                icon = pointIcon(point.child.type,
                    point.project, point.color,
                    point.child.underground_connection, point.content_count);
                if (point.project) {
                    l.off('click', onLayerClick);
                }
            } else {
                icon = pointIcon('gispoint', true);
                l.off('click', onLayerClick);
            }
            if (!l.options.icon?.options?.className?.includes('networks-point-container')) {
                l.setIcon(icon);
            }
        });

        // disable the lines that are part of a project
        // filteredLines.forEach((l) => {
        //     console.log(l.feature);
        // });
    });

    // Prevent selected objects from disappearing when the map is being moved
    useEffect(() => {
        editor.registerLoadingCallback(highlightCallBack);
        return () => {
            editor.unregisterAllLoadingCallback();
        };
    }, [group]);

    // Get all layers from map and filter gisPoints and GisLines
    // Attach/detach onClick event listener to all filtered layers
    useEffect(() => {
        const layers = Object.values(_layers);
        const filteredLayers = layers.filter((layer) => (layer.feature ? layer : ''));
        filteredLayers.forEach((l) => l.on('click', onLayerClick));

        disableObjects(filteredLayers);

        return () => {
            filteredLayers.forEach((l) => l.off('click', onLayerClick));
        };
    }, [_layers, map, onLayerClick, disableObjects]);

    const clearLinesHighlight = () => {
        const layers = Object.values(_layers);
        const filtered = layers.filter((layer) => (layer.feature ? layer : ''));
        const highlightedLines = filtered.filter((layer) => layer.feature.geometry.type === 'LineString');
        highlightedLines.forEach((line) => line.setStyle({ weight: 3 }));
    };

    const handleClose = () => {
        clearLinesHighlight();
        editor.unregisterAllLoadingCallback();
        editor._loadFeatures();
        map.grouping = false;
        history.push(`${match.url.split('/group')[0]}`);
    };

    return (
        <GroupActionsForm
            handleClose={handleClose}
            selectedLayers={group}
        />
    );
};

export default withLeaflet(GroupObjects);
